import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  ErrorInfo,
  Grid,
  IconButton,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  MUIDataTableMeta,
  Pill,
  SearchBar,
  TableActionTypes,
  TableState,
  Typography,
  useDataTable,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import FormHeader from '../../../../components/form-items/FormHeader';
import { useResitNavigation } from '../../../../routes/ResitRoute';
import { CoordinatorResitAction } from '../../logic/useCoordinatorResitData';


// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#candidate-table .MuiPaper-root': {
      borderRadius: '20px 0px 0px 20px !important',
      height: 'auto',
    },
    '#candidate-table .MuiTableCell-paddingCheckbox': {
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(2),
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: theme.spacing(7),
      },
    },
    '#candidate-table #table-clear-selection-button': {
      display: 'none',
    },
  },
  contentPadding: {
    paddingLeft: theme.spacing(7),
    [theme.breakpoints.down('md')]: {
      paddingLeft: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
    },
  },
  listTopRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  listTitle: {
    marginRight: theme.spacing(2),
  },
  searchInput: {
    width: '100%',
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  selectBar: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    backgroundColor: theme.color.background.bgMain,
    borderTopLeftRadius: '20px',
  },
  selectContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  flexBox: {
    flex: 1,
    marginLeft: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  deleteButton: {
    marginRight: theme.spacing(1),
  },
  leftPanel: {
    display: 'flex',
    flexDirection: 'column',
    border: 0,
    borderRightWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.color.border.borderMain,
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    height: '100%',
  },
  tableList: {
    flex: 1,
    height: '100%',
  },
  icon: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  detailButton: {
    '&:active': {
      backgroundColor: theme.color.background.bgActionHover,
    },
    '&:hover': {
      backgroundColor: theme.color.background.bgInverse01,
    },
    backgroundColor: theme.color.background.bgInverse02,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  actionButton: {
    color: theme.color.link.linkDefault01,
  },
  limitInfo: {
    paddingBottom: theme.spacing(2),
  },
}));

interface CandidateListingProps {
  candidateNoLimit: number;
  dispatch: React.Dispatch<CoordinatorResitAction>;
  candidateTableRows: {
    name: any;
    id: any;
  }[];
  rowsSelected: any[];
  setRowsSelected: React.Dispatch<React.SetStateAction<any[]>>;
  options: any;
  tableColumns: MUIDataTableColumnDef[];
  selectedCandidate: number | null;
  setSelectedCandidate: React.Dispatch<React.SetStateAction<number | null>>;
  isAddCandidate: boolean;
  setIsAddCandidate: (evt: any, val: boolean) => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const useLocalTableColumns = (
  candidateTableRows: {
    name: any;
    id: any;
  }[],
  tableState: TableState,
  selectedCandidate: number | null,
  setSelectedCandidate: React.Dispatch<React.SetStateAction<number | null>>
): MUIDataTableColumnDef[] => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  return [
    {
      name: 'name',
      label: t('resit->>CoordinatorResit.Candidate Name'),
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex: number, rowIndex: number) => {
          if (candidateTableRows.length < dataIndex + 1) {
            return null;
          }
          return (
            <Typography id={`text-candidateName-row${rowIndex}`}>
              {tableState.data[dataIndex].name}
            </Typography>
          );
        },
      },
    },
    {
      name: 'id',
      label: t('resit->>CoordinatorResit.NRIC/FIN/Passport No.'),
      options: {
        filter: false,
      },
    },
    {
      name: 'action',
      label: ' ',
      options: {
        customBodyRender: (value: any, tableMeta: MUIDataTableMeta, updateValue: any) => {
          // customBodyRender: (value: any, tableMeta: MUIDataTableMeta, updateValue: (value: any) => void)  => {
          const selected =
            selectedCandidate === tableState.size * tableState.page + tableMeta.rowIndex;
          return (
            (<IconButton
              id={`iconButton-candidateListing-row${tableMeta.rowIndex}`}
              className={selected ? classes.detailButton : ''}
              iconSize="L"
              key={tableMeta.rowData[1]}
              color={selected ? theme.color.icon.iconInverse : theme.color.icon.iconSelected}
              name={IconName.arrowRight}
              ariaLabel="go-detail-click"
              onClick={() => {
                if (selected) {
                  setSelectedCandidate(null);
                } else {
                  setSelectedCandidate(tableState.size * tableState.page + tableMeta.rowIndex);
                }
              }}
            // iconSize="L" 
            />)
          );
        },
      },
    },
  ];
};

const CandidateListing = ({
  candidateNoLimit,
  dispatch,
  candidateTableRows,
  rowsSelected,
  setRowsSelected,
  options,
  tableColumns,
  selectedCandidate,
  setSelectedCandidate,
  isAddCandidate,
  setIsAddCandidate,
  step,
  setStep,
}: CandidateListingProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const history = useHistory();
  const { toCoordinatorResitRegister } = useResitNavigation();
  const { state } = useLocation<any>();

  const [showLimitError, setShowLimitError] = useState(false);

  const { state: tableState, dispatch: tableDispatch, tOptions, selectionToolbar } = useDataTable();

  const customOptions = useMemo<MUIDataTableOptions>(
    () => ({
      ...tOptions,
      ...options,
      pagination: true,
      selectToolbarPlacement: 'replace',
      tableId: 'candidateListing',
    }),
    [options, tOptions]
  );

  const selectedNRICs = useMemo(
    () => Object.keys(tableState.rowsSelected).map((i) => tableState.rowsSelected[i].id),
    [tableState.rowsSelected]
  );

  const localTableColumns = useLocalTableColumns(
    candidateTableRows,
    // @ts-ignore
    tableState,
    selectedCandidate,
    setSelectedCandidate
  );

  const bulkActions = useMemo(
    () => [
      <Button
        key="deleteCandidateButton"
        id="button-candidateListing-deleteCandidates"
        size="medium"
        variant="text"
        onClick={() => {
          setSelectedCandidate(null);
          setRowsSelected([]);
          dispatch({
            type: 'DELETE_CANDIDATES',
            payload: selectedNRICs,
          } as CoordinatorResitAction);
          tableDispatch({
            type: 'CLEAR_SELECTION',
          } as TableActionTypes);
          tableDispatch({
            type: 'UPDATE_PAGE',
            payload: 0,
          } as TableActionTypes);
          setShowLimitError(false);
        }}
      >
        Delete Candidates
      </Button>,
    ],
    [dispatch, selectedNRICs, setRowsSelected, setSelectedCandidate, tableDispatch]
  );

  useEffect(() => {
    const startPoint = tableState.page * tableState.size;
    const filterData = candidateTableRows.filter((user) => user.name.includes(tableState.search));
    const pageData = filterData.slice(startPoint, startPoint + tableState.size);

    tableDispatch({
      type: 'UPDATE_DATA',
      payload: {
        data: pageData,
        count: filterData.length,
      },
    } as TableActionTypes);
  }, [tableState.page, tableState.size, tableState.search, candidateTableRows, tableDispatch]);

  return (
    (<Box className={classes.leftPanel}>
      <Box className={classes.listTopRow}>
        <FormHeader
          title="Select Candidates"
          action={<Pill variant="info" label={candidateTableRows.length.toString()} />}
        />
      </Box>
      <Grid container justify="center" alignItems="center">
        <Grid xs={6}>
          <Box className={classes.buttonContainer}>
            <Button
              id="button-candidateListing-addCandidate"
              size="medium"
              variant="text"
              className={classes.actionButton}
              onClick={(evt) => {
                const hitLimit = candidateTableRows.length >= candidateNoLimit;
                setShowLimitError(hitLimit);

                if (!hitLimit) {
                  if (step === 0) {
                    if (selectedCandidate !== null) {
                      setSelectedCandidate(null);
                      setIsAddCandidate(evt, true);
                    } else {
                      setIsAddCandidate(evt, !isAddCandidate);
                    }
                  } else {
                    setStep(0);
                    setSelectedCandidate(null);
                    setIsAddCandidate(evt, true);
                  }
                }
              }}
            >
              Add Candidate
            </Button>
          </Box>
        </Grid>
        <Grid xs={6}>
          <Box className={classes.buttonContainer}>
            <Button
              id="button-candidateListing-reuploadCSVFile"
              size="medium"
              variant="text"
              className={classes.actionButton}
              onClick={() =>
                history.push(toCoordinatorResitRegister, {
                  selectedRegisterOption: state.selectedRegisterOption,
                  selectedExamLevel: state.selectedExamLevel,
                })
              }
            >
              Bulk Upload
            </Button>
          </Box>
        </Grid>

        {showLimitError && (
          <Grid xs={12} className={classes.limitInfo}>
            <ErrorInfo
              label={`You may only add up to ${candidateNoLimit} candidate(s) at a time.`}
            />
          </Grid>
        )}
      </Grid>
      <SearchBar
        fullWidth
        className={classes.searchInput}
        error={false}
        name="searchInput"
        ariaLabel="searchInput"
        placeholder={t('resit->>CoordinatorResit.Search for Name')}
        defaultValue={tableState.search}
        onSearch={(str) => {
          tableDispatch({
            type: 'UPDATE_SEARCH',
            payload: str,
          } as TableActionTypes);
        }}
      />
      <Box id="candidate-table" className={classes.tableList}>
        <MUIDataTable
          // selection state will be override by local one, only needed pagination state
          // @ts-ignore
          state={tableState}
          dispatch={tableDispatch}
          columns={localTableColumns}
          // @ts-ignore
          data={tableState.data}
          // don't render default toolbar select, using external one
          components={{ TableToolbarSelect: () => selectionToolbar(bulkActions) }}
          options={customOptions}
        />
      </Box>
      <br />
    </Box>)
  );
};

export default CandidateListing;
